import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Upload, Link2, Sparkles, Search } from "lucide-react";
import { toast } from "sonner";
import { describeError } from "@/lib/api";
import { uploadEob } from "@/lib/eobEraV2";
import {
  listChecks, aggregates, filterChecks, isToday, fetchCheckProc, computeLinkage, sinceFor, RANGES,
} from "@/lib/posting";
import { MoneyCards } from "@/components/posting/MoneyCards";
import { FilterBar } from "@/components/posting/FilterBar";
import { ChecksList } from "@/components/posting/ChecksList";
import { StreamingPanel } from "@/components/posting/StreamingIn";
import { UploadDropzone } from "@/components/posting/UploadDropzone";
import { LinkagePanel } from "@/components/posting/LinkagePanel";
import { EraDrawer } from "@/components/posting/EraDrawer";
import { PostingEmptyState } from "@/components/posting/PostingEmptyState";
import "./posting.css";

export default function PaymentPosting() {
  const reqRef = useRef(0);

  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [recentIds, setRecentIds] = useState([]); // just-uploaded file_ids — kept pinned to top
  const [panelOpen, setPanelOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [range, setRange] = useState("2d");
  const [linkPanel, setLinkPanel] = useState({ open: false, section: "payees" });
  const [eraDrawer, setEraDrawer] = useState({ open: false, check: null });
  const [filters, setFilters] = useState({ status: "all", payer: "", q: "", amtMin: "", amtMax: "", dateFrom: "", dateTo: "", batch: false });

  // Always fetch all-time — money cards + linkage are all-time; the range chips
  // filter the list client-side (see `rows`).
  const fetchChecks = useCallback(async (silent = false) => {
    const rid = ++reqRef.current;
    if (!silent) { setLoading(true); setError(null); }
    try {
      const data = await listChecks({ since: "all" });
      if (reqRef.current !== rid) return;
      setChecks(data);
    } catch (err) {
      if (reqRef.current === rid && !silent) setError(describeError(err));
    } finally {
      if (reqRef.current === rid && !silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChecks(false); }, [fetchChecks]);

  const linkage = useMemo(() => computeLinkage(checks.map((c) => c.raw)), [checks]);

  const anyLive = checks.some((c) => c.postState === "processing") || uploads.some((u) => u.state !== "error");
  useEffect(() => {
    if (!anyLive) return;
    const iv = setInterval(() => fetchChecks(true), 2500);
    return () => clearInterval(iv);
  }, [anyLive, fetchChecks]);

  useEffect(() => {
    if (!uploads.length) return;
    const ids = new Set(checks.map((c) => c.id));
    setUploads((u) => u.filter((x) => !(x.fileId && ids.has(x.fileId))));
  }, [checks]); // eslint-disable-line react-hooks/exhaustive-deps

  const runUpload = useCallback(async (tempId, file) => {
    setUploads((u) => u.map((x) => (x.tempId === tempId ? { ...x, state: "uploading", pct: 0, error: null } : x)));
    try {
      const done = await uploadEob(file, {
        onProgress: (p) => setUploads((u) => u.map((x) => (x.tempId === tempId ? { ...x, pct: p } : x))),
      });
      setUploads((u) => u.map((x) => (x.tempId === tempId ? { ...x, state: "processing", fileId: done.file_id } : x)));
      setRecentIds((r) => (r.includes(done.file_id) ? r : [...r, done.file_id]));
      fetchChecks(true);
    } catch (err) {
      setUploads((u) => u.map((x) => (x.tempId === tempId ? { ...x, state: "error", error: describeError(err) } : x)));
      toast.error(`${file.name}: ${describeError(err)}`);
    }
  }, [fetchChecks]);

  const startUpload = useCallback((file) => {
    const tempId = `up_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setUploads((u) => [...u, { tempId, name: file.name, pct: 0, state: "uploading", file }]);
    runUpload(tempId, file);
  }, [runUpload]);

  const retryUpload = useCallback((u) => { if (u?.file) runUpload(u.tempId, u.file); }, [runUpload]);
  const dismissUpload = useCallback((tempId) => setUploads((u) => u.filter((x) => x.tempId !== tempId)), []);

  const queueFiles = useCallback((valid) => {
    if (!valid?.length) return;
    toast.success(`Queued ${valid.length} document${valid.length === 1 ? "" : "s"} — Lens is on it. You can keep working.`);
    valid.forEach(startUpload); // fire in parallel — the API queues by client config
    setPanelOpen(true);
  }, [startUpload]);

  const stats = useMemo(() => aggregates(checks), [checks]);
  const statusCounts = useMemo(() => {
    const c = { all: checks.length };
    for (const chk of checks) c[chk.postState] = (c[chk.postState] || 0) + 1;
    c.batch = checks.filter((chk) => isToday(chk.createdAt)).length;
    return c;
  }, [checks]);
  const rows = useMemo(() => {
    const cutoff = sinceFor(range);
    const inRange = (c) => cutoff === "all" || (c.createdAt && new Date(c.createdAt) >= new Date(cutoff));
    const filtered = filterChecks(checks, filters).filter((c) => c.postState !== "processing" && inRange(c));
    // Just-uploaded (now-processed) checks stay pinned to the top; then unapplied desc.
    const rank = (c) => (recentIds.includes(c.id) ? 0 : 1);
    return [...filtered].sort((a, b) => rank(a) - rank(b) || b.unapplied - a.unapplied);
  }, [checks, filters, recentIds, range]);
  const streamingChecks = useMemo(() => checks.filter((c) => c.postState === "processing"), [checks]);
  const streamCount = uploads.length + streamingChecks.length;
  const [batchTotal, setBatchTotal] = useState(0);
  const processed = Math.max(0, batchTotal - streamCount);
  const showEmptyAll = !loading && !error && checks.length === 0 && uploads.length === 0;

  // Poll the single-file endpoint for each streaming check to surface the exact
  // stage/message the API returns (the list endpoint doesn't include `progress`).
  const [procMap, setProcMap] = useState({});
  const streamIds = streamingChecks.map((c) => c.id).join(",");
  useEffect(() => {
    if (streamCount === 0) {
      if (batchTotal > 0) {
        toast.success(`All ${batchTotal} document${batchTotal === 1 ? "" : "s"} processed ✓ — ready to post.`);
        setBatchTotal(0);
      }
      setPanelOpen(false);
    } else {
      setBatchTotal((t) => Math.max(t, streamCount));
    }
  }, [streamCount]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const ids = streamIds ? streamIds.split(",") : [];
    if (!ids.length) return;
    let alive = true;
    const tick = async () => {
      const entries = await Promise.all(ids.map((id) => fetchCheckProc(id).then((p) => [id, p]).catch(() => null)));
      if (!alive) return;
      setProcMap((m) => { const n = { ...m }; for (const e of entries) if (e) n[e[0]] = e[1]; return n; });
    };
    tick();
    const iv = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(iv); };
  }, [streamIds]);

  const viewEra = (c) => setEraDrawer({ open: true, check: c });
  const manage = () => toast.message("Post Payment workspace is coming in the next pass (API under development).");
  const forceApply = () => toast.message("Force Apply is a mock action — no posting API yet.");
  const history = () => toast.message("Applied History is a mock view — no posting API yet.");
  const reviewGap = (c) => setEraDrawer({ open: true, check: c });
  const acceptAllReady = () => toast.success(`Accepted ${stats.ready.n} ready check${stats.ready.n === 1 ? "" : "s"} (mock — no posting API).`);

  // Linkage trigger summary (panel default opens on whichever category is most pending).
  const pend = (s) => Math.max(0, (s?.total || 0) - (s?.ready || 0));
  const linkPending = pend(linkage.stats.payeesAligned) + pend(linkage.stats.payersLinked) + pend(linkage.stats.payeeNpis);
  const linkDefaultSection = [
    ["payees", pend(linkage.stats.payeesAligned)],
    ["payers", pend(linkage.stats.payersLinked)],
    ["npis", pend(linkage.stats.payeeNpis)],
  ].sort((a, b) => b[1] - a[1])[0][0];

  return (
    <div className="pp-wrap px-6 py-6 fade-up" data-testid="payment-posting-root">
      {/* Header: title + actions */}
      <div className="pp-header">
        <div className="pp-header-l">
          <h1>Payment Posting</h1>
        </div>
        <div className="pp-right">
          {streamCount > 0 ? (
            <button className="pp-sp-trigger" onClick={() => setPanelOpen(true)} data-testid="pp-streaming-trigger">
              <span className="pp-livedot" />
              <span data-testid="pp-batch-progress">{processed} of {batchTotal} processed</span>
              <span className="pp-sp-tct" data-testid="pp-streaming-count">{streamCount}</span>
            </button>
          ) : null}
          <button className="pp-btn pp-ghost" onClick={() => setLinkPanel({ open: true, section: linkDefaultSection })} data-testid="pp-linkage-trigger">
            <Link2 size={14} /> Linkage
            {linkPending > 0 ? <span className="pp-linkcount" data-testid="pp-linkage-pending-count">{linkPending}</span> : null}
          </button>
          <button className="pp-btn pp-ghost" onClick={() => toast.message("Posting Rules workspace — coming soon.")} data-testid="pp-posting-rules-btn">
            Posting Rules →
          </button>
          <button className="pp-btn pp-dark" onClick={() => setDropOpen(true)} data-testid="pp-add-check-btn">
            <Upload size={14} /> ＋ Add New Check
          </button>
        </div>
      </div>

      {/* Range chips + search — pushed to the top for max list real estate */}
      <div className="pp-rangebar" data-testid="pp-rangebar">
        <div className="pp-ranges">
          {RANGES.map((r) => (
            <button key={r.id} className={`pp-range ${range === r.id ? "pp-on" : ""}`}
              onClick={() => setRange(r.id)} data-testid={`pp-range-${r.id}`}>
              {r.label}
            </button>
          ))}
        </div>
        <div className="pp-searchbar" data-testid="pp-searchbar">
          <span className="pp-spark"><Sparkles size={15} /></span>
          <input value={filters.q || ""} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Search filename, payer or check no…" data-testid="pp-search-input" />
          {filters.q ? (
            <button className="pp-allf" onClick={() => setFilters((f) => ({ ...f, q: "" }))} data-testid="pp-search-clear">Clear</button>
          ) : <Search size={15} style={{ color: "var(--sub)", flex: "none" }} />}
        </div>
        <div className="pp-matches" data-testid="pp-match-count">{rows.length} match{rows.length === 1 ? "" : "es"}</div>
      </div>

      <FilterBar filters={filters} setFilters={setFilters} counts={statusCounts} />
      <MoneyCards stats={stats} loading={loading} onAcceptAllReady={acceptAllReady} />

      {loading ? (
        <SkeletonList />
      ) : error ? (
        <div className="pp-list" style={{ padding: "56px 20px", textAlign: "center" }}>
          <div style={{ color: "var(--red)", fontWeight: 700, fontSize: 13 }}>Couldn't load checks</div>
          <div style={{ color: "var(--sub)", fontSize: 11, marginTop: 4 }}>{error}</div>
          <button className="pp-btn pp-dark" style={{ marginTop: 12 }} onClick={() => fetchChecks(false)}>Retry</button>
        </div>
      ) : showEmptyAll ? (
        <div className="pp-list"><PostingEmptyState variant="empty" onImport={() => setDropOpen(true)} /></div>
      ) : rows.length > 0 ? (
        <ChecksList checks={rows} uploads={[]}
          onManage={manage} onViewEra={viewEra} onForceApply={forceApply} onHistory={history} onReviewGap={reviewGap} />
      ) : streamingChecks.length || uploads.length ? null : (
        <div className="pp-list"><PostingEmptyState variant="filter" /></div>
      )}

      <StreamingPanel open={panelOpen} onClose={() => setPanelOpen(false)}
        uploads={uploads} checks={streamingChecks} procMap={procMap}
        onRetry={retryUpload} onDismiss={dismissUpload} />

      <UploadDropzone open={dropOpen} onClose={() => setDropOpen(false)} onConfirm={queueFiles} />

      <LinkagePanel open={linkPanel.open} section={linkPanel.section} data={linkage}
        onSection={(section) => setLinkPanel((p) => ({ ...p, section }))}
        onClose={() => setLinkPanel((p) => ({ ...p, open: false }))} />

      <EraDrawer open={eraDrawer.open} check={eraDrawer.check}
        onClose={() => setEraDrawer((d) => ({ ...d, open: false }))} />
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="pp-list" data-testid="pp-skeleton">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="pp-row pp-ck2">
          <div className="pp-edge" style={{ background: "rgba(0,0,0,.08)" }} />
          <div><div className="pp-shimmer" style={{ height: 13, width: "55%", borderRadius: 6 }} /><div className="pp-shimmer" style={{ height: 11, width: "38%", borderRadius: 6, marginTop: 8 }} /></div>
          <div className="pp-shimmer" style={{ height: 52, borderRadius: 11 }} />
          <div className="pp-shimmer" style={{ height: 52, borderRadius: 11 }} />
          <div className="pp-shimmer" style={{ height: 30, width: 100, borderRadius: 9, justifySelf: "end" }} />
        </div>
      ))}
    </div>
  );
}
